import type { TenantConfig } from '@pink/shared';

/**
 * Hartkodierte Tenant-Configs für den `VITE_MOCK_MODE`-Pfad.
 *
 * Dient ausschließlich dem Pilot-Demo-Modus: Stakeholder und Pilotpraxen
 * sollen das visuelle Konzept abnehmen können, bevor Crypto, Pinning und
 * Token-Routing fertig sind.
 *
 * UUIDs und Public-Keys sind **1:1 die Werte aus den echten Tenant-Configs**
 * unter `nginx/tenant-configs/<id>.json`. Damit deckt der Mock-Modus dieselben
 * Mandanten ab wie der Docker-Stack — und der Mock-Praxis-Consumer kann
 * gegen dieselben Schlüssel testen (`nginx/keys/<host>.local/keypair/`).
 *
 * Produktion deaktiviert den Mock-Pfad über die Vite-ENV (`VITE_MOCK_MODE`
 * unset oder `false`).
 *
 * `MOCK_TENANT_OPTIONS` listet alle umschaltbaren Demo-Mandanten für die
 * DemoBar; `MOCK_TENANT_CONFIG` ist der initiale Wert für den
 * `ConfigProvider`-Bootstrap.
 *
 * **Quelle der vier Demo-Praxen**: Lovable-Vorgabe. Brandfarben sind 1:1 die
 * Lovable-Hex-Werte; `--brand-soft` leitet CSS-seitig per
 * `color-mix(in oklch, …, white)` aus dem Hex-Primary ab.
 */

const PUBLIC_KEY_DIE_RADIOLOGIE =
  'MIIBojANBgkqhkiG9w0BAQEFAAOCAY8AMIIBigKCAYEAz0RHpP00vf+NqRRMeLK8kDviFBw5n1iJRPHnLhjd2nVDdW/u/z7hsWDTFKNGO+wfraF7fqUKDOsh0TsM+vu4iSOaYsgQd0yM29tGah0K7vstWLLgXbfXmDYShuyVW2A9eaWsSjwQKNQ/iTKWTaxx8Eiidk2T8tKFpMr197uhUvvVLVU4n2FTbYHKcUIVZrizKJ0DBeP3W7bZjy/34MbOM7vO/3AVVfhMiQkeEyuHXv1ifaIyffaoTNwDDJotkwA3z02FS+U2Zg5mcMjFNSkXC9VJXQC/dfz/LJfHmY/B+1oYia6W5wBSWuVJpWsfZM20QMqCckXc9WsN88Qz0owZZ1JYJrg42L2u4i97cfut6BaOr3x97ab5p3PmEn82+meu0VfVwHFgND0HIojAx34erv7nHpzfciHCoA0CN2LsA0olXBkEO1Qft70CoSIwjTbheMCud1YZeU+5R4CBZ5AtXdgnJ+DnkVsLAtMc1gPWSUpPtRMpn0hpy8O6pCF89HIzAgMBAAE=';
const PUBLIC_KEY_DUESSELDORF =
  'MIIBojANBgkqhkiG9w0BAQEFAAOCAY8AMIIBigKCAYEA2fcpZ68Fz4Gd5VjXdlvErX+63P2qITmLp6QczZlxmh0VlP1/FLRJMzI3uwEjk/Y4O3OL/Byrob4UC2gCHjF2kfgIGhYWE4PXplvOgHP1CKIYzjli5RyAJCi3LhA7ednx3575q0VA7GWSSXZYASvNFEpkjdUmkHj9wUD1iLLp3cZ1csstVP8YWgpjipL0S99FUWOI+sq1V79qJKjWgPLzbk3QrKW3p2fPghNzuSG+YmqoCq9FeVEWc9aFg7RRvpm9uVcbGn2TXxPAKvmZa40FNz6Dk9IB1EWqoDJdhHdchrVspoM9lb7gPOBiNz3ShWtD5VPQj8MLuIAGj3HZ24apIw72MrW1I6fioRkmMD52Z+cEdmhIEqpYfWg/lJOyj/CPJSqgd+QyRoq/dA9iCR+bYq6UtI57/2q+sCuuGBbXdASvPrH3dDqPWMM46EqdTAwDwKS4flG8KLjo4F77Cclf8bzs4KbiGvXC0MItLsnK0Q+sj81cvoXBaguP1E+PCkq9AgMBAAE=';
const PUBLIC_KEY_HANNOVER =
  'MIIBojANBgkqhkiG9w0BAQEFAAOCAY8AMIIBigKCAYEAy9jC9LjeQkuJZgiWLuFlsOPkdnnJs3NQEv3Dyo11GGOGebGdK3Sxe+j2O2kKT1B3rUa9h3kS4iMJrkxhh13C0XM/bdxJ0KqD41Uhy0s4AawDcTrjPqx7HPccFca5vjkld+A/yVDJeIN+wlTgFc2j4ksHt6ybBuRjIx+/NgHjMBaoYYnzLDV4x1FLPJETliwSuPYG/MVUNwCI0V30/MCvVM+q5wZY4MMUH6NRjn3E53NGgsE8INTuuQ8cIOVKWoqALpkJai8ONFiuezEgtrGYSZTd47TBsnK6h7s6ms94DWlwB3LUWwOHLfzB9zYb5V3C9767HvFiI993Qx/v2g4gBNNElGS35JbE8EWWR3a76hhxg0FuCXbPtCYmYt9Ot0m+lsV3V2ajgBj4+250AMVzU6OxAxHLLeYJCnM8vI6bcb8KhAEJnHUngz9Cq1+3znIZwXQog9W6h6UQM0NttSQ5aPiteanYIJY9sNi1WTeDXDvDRdDjdZMcWIO5QqL2dSxBAgMBAAE=';
const PUBLIC_KEY_ALLIANZ =
  'MIIBojANBgkqhkiG9w0BAQEFAAOCAY8AMIIBigKCAYEAwrPwHx/gCudDh5WI3H8yX7j9zRwDQSa6oVMTmNC2YPX23T8fMfUWjVuGZyn/cGj2bg3E0aIWgSr8KkX3EUkNUGtVFikARP47ohG1hR8+MK6XQFvvXRyoiM1n8h7uNhs6BSdLC5H1TVcjupWcC3vl1KFubLovy5PtielPAqhLxMdG8ZBCPRPy7zAFunD/6CJio4XaLBiwUDytS+miiBG6Mejd67tZzcg1FC+ppwuNbjB58C2onVElfe5Jh4P1IkaXKIRBnpWeQ9P5cvL0jU4VMyrsp1TPK7MtluidmOsAmcWZDqiE3EBkiM0CxNHM4vQ0irr08acX7EZ1ek2JPQXrSdPucDAASBQe0uoF+wjC0Nwfx6wLApqguqn8FmUDx4uOBTLNGO0WS5pQjF1n0ZRW0od9TFUre7fq/B13XFPupPXlhCmF6ZWw38cNrFhd0kTVOo9AUSpUflUeaN7K7JvnnIbLj/3Pep9qKvnxzMn6OQbLlUfH2DKaTQiBw3qJ6m+xAgMBAAE=';

export interface MockTenantOption {
  /** Stabile Kennung für den Tenant-Switcher der DemoBar. */
  readonly id: string;
  /** Anzeigename des Mandanten in der DemoBar (kein i18n — reines Dev-Tool). */
  readonly label: string;
  readonly config: TenantConfig;
}

/**
 * Default-Demo-Mandant. Erstes Element der DemoBar-Optionsliste — so bleibt
 * die Default-Identität im Typsystem fest.
 */
const DEFAULT_MOCK_TENANT: MockTenantOption = {
  id: 'die-radiologie',
  label: 'Die Radiologie',
  config: {
    tenantId: '0bfce48c-5698-4786-97ff-9d8161b080f7',
    tenantName: 'Die Radiologie',
    imprintUrl: 'https://www.die-radiologie.de/impressum',
    tenantPublicKeys: [PUBLIC_KEY_DIE_RADIOLOGIE],
    logoUrl: '/assets/logo-die-radiologie.svg',
    phoneNumber: '+49 30 12345678',
    themeColors: {
      primary: '#FF8200',
      secondary: '#4B5054',
      background: '#ffffff',
      text: '#1a1a1a',
    },
  },
};

/**
 * Demo-Mandanten der DemoBar (Lovable-Vorgabe).
 *
 * Brandfarben sind die Lovable-Hex-Werte; bei sehr hellem Lime
 * (`Röntgeninstitut Düsseldorf`) erzwingt `textOnPrimary` einen dunklen
 * Vordergrund auf Brand-Hintergrund, weil Weiß den WCAG-Kontrast verletzt.
 */
export const MOCK_TENANT_OPTIONS: readonly MockTenantOption[] = [
  DEFAULT_MOCK_TENANT,
  {
    id: 'roentgeninstitut-duesseldorf',
    label: 'Röntgeninstitut Düsseldorf',
    config: {
      tenantId: '492ee288-8a2f-498f-acd9-2201b91d1e14',
      tenantName: 'Röntgeninstitut Düsseldorf',
      imprintUrl: 'https://www.roentgeninstitut-duesseldorf.de/impressum',
      tenantPublicKeys: [PUBLIC_KEY_DUESSELDORF],
      logoUrl: '/assets/logo-roentgeninstitut-duesseldorf.svg',
      phoneNumber: '+49 211 87654321',
      themeColors: {
        primary: '#CAD100',
        secondary: '#5E6A70',
        background: '#ffffff',
        text: '#1a1a1a',
        textOnPrimary: '#1a1a1a',
      },
    },
  },
  {
    id: 'roentgenpraxis-hannover',
    label: 'Röntgenpraxis Hannover',
    config: {
      tenantId: 'b1e1508b-01ba-479b-a124-9bc7e6ef7744',
      tenantName: 'Röntgenpraxis Hannover',
      imprintUrl: 'https://www.roentgenpraxis-hannover.de/impressum',
      tenantPublicKeys: [PUBLIC_KEY_HANNOVER],
      logoUrl: '/assets/logo-roentgenpraxis-hannover.svg',
      phoneNumber: '+49 511 11223344',
      themeColors: {
        primary: '#FF5500',
        secondary: '#475569',
        background: '#ffffff',
        text: '#1a1a1a',
      },
    },
  },
  {
    id: 'radiologische-allianz',
    label: 'Radiologische Allianz',
    config: {
      tenantId: 'fd3bc7f2-ad9c-4a91-a09d-69b8759a305b',
      tenantName: 'Radiologische Allianz',
      imprintUrl: 'https://www.radiologische-allianz.de/impressum',
      tenantPublicKeys: [PUBLIC_KEY_ALLIANZ],
      logoUrl: '/assets/logo-radiologische-allianz.svg',
      phoneNumber: '+49 40 99887766',
      themeColors: {
        primary: '#E32C1E',
        secondary: '#475569',
        background: '#ffffff',
        text: '#1a1a1a',
      },
    },
  },
];

export const MOCK_TENANT_CONFIG: TenantConfig = DEFAULT_MOCK_TENANT.config;
